import React, { useEffect, useState } from "react";
import styles from "./ListUpdate.module.css";
import { PortfolioSearchService } from "../../../../../../api";
import { isNonEmptyArray, multiSort } from "../../../../../../common/utils";
import { useApi, useEffectOnce } from "../../../../../../common/hooks";
import { SearchInput } from "../../../../../../common/components";
import ExpandableRow from "./ExpandableRow";

const filterNestedArray = (input, data) => {
	if (!input) {
		return data;
	}
	const lowercasedInput = input.toLowerCase();
	function filterHelper(items) {
		return items
			.map((item) => {
				const hasMatchingDescendants = item?.subitems && filterHelper(item.subitems).length > 0;
				const isMatch = item.value.toLowerCase().includes(lowercasedInput);
				if (isMatch || hasMatchingDescendants) {
					return {
						...item,
						subitems: hasMatchingDescendants ? filterHelper(item.subitems) : [],
					};
				}
				return null;
			})
			.filter(Boolean);
	}
	return filterHelper(data);
};

export default function ListUpdate({
	currentValue,
	fieldUuid,
	isMultipleChoice = false,
	open,
	selectedPageId,
	onChange,
}) {
	const [localValue, setLocalValue] = useState([]);
	const [items, setItems] = useState([]);
	const [tempLocalValue, setTempLocalValue] = useState([]);
	const [hasChecked, setHasChecked] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const { call: getFieldPossibleValues } = useApi(PortfolioSearchService.getFieldPossibleValues);
	useEffect(() => {
		getFieldPossibleValues({ pageId: selectedPageId, uuid: fieldUuid })
			.then((data) => {
				if (isNonEmptyArray(data?.possibleValues)) {
					setItems(data?.possibleValues);
				}
			})
			.catch(console.error);
	}, [selectedPageId, fieldUuid, getFieldPossibleValues]);
	useEffect(() => {
		if (open) {
			setTempLocalValue(localValue);
		}
	}, [open, localValue]);
	useEffectOnce(
		() => {
			const initialValues = items
				.filter((item) => currentValue.some((val) => val.value === item.value))
				.map((item) => item?.value);
			setLocalValue(initialValues);
			setTempLocalValue(initialValues);
		},
		[currentValue, items],
		() => currentValue && isNonEmptyArray(items)
	);
	const handleClearSearchValue = () => {
		setSearchValue("");
	};
	const handleChangeSearchValue = (event) => {
		const { value } = event.target;
		setSearchValue(value);
	};
	const handleIsChecked = (item) => tempLocalValue.includes(item.value);
	const handleCheck = (item) => {
		if (isMultipleChoice) {
			if (tempLocalValue.includes(item.value)) {
				setTempLocalValue((prev) => prev.filter((savedItem) => savedItem !== item.value));
			} else {
				setTempLocalValue((prev) => [...prev, item.value]);
			}
		} else if (!tempLocalValue.includes(item.value)) {
			setTempLocalValue([item.value]);
		}
		setHasChecked(true);
	};
	useEffect(() => {
		if (hasChecked) {
			onChange(items.filter((currentItem) => tempLocalValue.includes(currentItem.value)));
			setHasChecked(false);
		}
	}, [hasChecked, items, onChange, tempLocalValue]);

	return (
		<div className={styles.container}>
			<div>
				<SearchInput
					fullWidth
					className={styles.searchContainer__textfield}
					value={searchValue}
					onChange={handleChangeSearchValue}
					onClearSearch={handleClearSearchValue}
				/>
				<div className={styles.tree}>
					{isNonEmptyArray(items) &&
						filterNestedArray(searchValue, multiSort(items, ["value"])).map((item) => (
							<ExpandableRow
								key={item.valueUuid}
								checkedItems={tempLocalValue}
								isMultipleChoice={isMultipleChoice}
								item={item}
								onCheck={handleCheck}
								onIsChecked={handleIsChecked}
							/>
						))}
				</div>
			</div>
		</div>
	);
}
